import {
  type MouseEvent,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import type { SpanNode } from '@repo/shared';
import { ChevronDown, ChevronRight } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { TraceContext } from '@/routes/(app)/(tracing)/context/trace-context';
import { formatDuration } from '@/routes/(app)/(tracing)/lib/formatter';

export const TreeNode = ({
  node,
  depth = 0,
}: {
  node: SpanNode;
  depth?: number;
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const hasChildren = node.children.length > 0;
  const { setOpenDetail, setSpan, openDetail, span } = useContext(TraceContext);

  const containerRef = useRef<HTMLDivElement>(null);

  const widthPercentage = Math.min((node.relativePercentage ?? 0) * 100, 100);

  const handleOpen = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setSpan(node);
      if (openDetail && node.id !== span?.id) return;
      setOpenDetail(true);
    },
    [node, openDetail, setOpenDetail, setSpan, span?.id]
  );

  const handleExpand = useCallback(() => {
    setIsExpanded(!isExpanded);
  }, [isExpanded]);

  return (
    <div ref={containerRef}>
      <Button
        variant={'ghost'}
        className={cn(
          'relative flex w-full items-center justify-start gap-2 py-3 pr-1 text-sm',
          {
            'text-accent-foreground bg-accent': span?.id === node.id,
          }
        )}
        onClick={handleOpen}
        style={{
          paddingLeft: `${depth > 0 ? depth * 35 + 28 - depth * 3 : 24}px`,
        }}>
        {hasChildren ? (
          <Button
            variant={'ghost'}
            onClick={handleExpand}>
            <Button.Icon icon>
              {isExpanded ? <ChevronDown /> : <ChevronRight />}
            </Button.Icon>
          </Button>
        ) : null}
        <div className='flex w-full gap-4'>
          <span
            className={cn('max-w-[250px] text-sm', {
              'px-1': depth === 0,
              truncate:
                containerRef.current?.offsetWidth &&
                containerRef.current.offsetWidth < 450 &&
                depth > 1 &&
                node.name.length > 16,
            })}>
            {node.name}
          </span>

          {node.duration > 0 && (
            <div className='relative w-full'>
              <div className='absolute top-[11px] h-px w-full bg-[hsl(220,14%,19%)]' />
              <div
                style={{
                  width: `${widthPercentage}%`,
                  left: 0,
                }}
                className={cn(
                  'bg-chart-1 absolute top-[9px] z-0 h-[5px] rounded-[5px] transition-all',
                  node.name.includes('agent') && 'bg-chart-2 left-1/2',
                  node.name.includes('llm') && 'bg-chart-3',
                  node.name.includes('ai') && 'bg-chart-4 left-2/4 w-5'
                )}></div>
              <span className='text-muted-foreground absolute left-0 top-3 font-mono text-[0.63rem]'>
                {formatDuration(node.duration)}ms
              </span>
            </div>
          )}
        </div>
      </Button>

      {hasChildren && isExpanded && (
        <>
          {node.children.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
            />
          ))}
        </>
      )}
    </div>
  );
};
